import { AlertCircle, ArrowLeft, Lock, SearchX, WifiOff } from 'lucide-react';
import { BORDER_COLOR } from '../../styles/tokens';
import { motivoDeCarga } from '../../utils/motivoDeCarga';

export { motivoDeCarga };  // re-export: las 5 pantallas importan las dos cosas de un solo lugar

/**
 * ═══════════════════════════════════════════════════════════════════════════════════════════
 * B-1 — CUANDO UN DETALLE NO SE PUEDE ABRIR, LA PANTALLA LO DICE. NO REBOTA EN SILENCIO.
 * ═══════════════════════════════════════════════════════════════════════════════════════════
 *
 * 🔴 Lo destapó A-1. Al ponerle un enlace directo a los correos, abrir uno que apuntaba a una
 * tarjeta inexistente **redirigía a la lista sin decir nada**, y el enlace parecía roto aunque
 * el motivo fuera legítimo. Las seis pantallas a las que A-1 enlaza fallaban de **tres maneras
 * distintas**: dos con un "Recibo no encontrado" plano —que dice lo mismo ante un 500 o una
 * caída de red—, una avisando solo en 409, y **tres redirigiendo mudas**.
 *
 * ⚠⚠ EL MENSAJE LO PONE EL BACKEND, NO ESTA PANTALLA. Los controladores ya distinguen el caso y
 * ya escriben en español: `"Tarjeta no encontrada."`, `"No autorizado."`, `"Solicitud no
 * encontrada."`, `"Acceso no autorizado."`, `"Recibo no encontrado."`. El frontend los tiraba a
 * la basura y hacía lo mismo con todos. Reescribirlos acá sería una **segunda expresión** del
 * mismo hecho, que es el defecto que este proyecto viene cerrando pieza por pieza.
 *
 * 🔴 Y POR ESO LA PANTALLA NO ASUME QUE 403 EXISTE. Gastos **colapsa a propósito** el "no
 * existe" y el "no es tuyo" en un 404 (V76: el detalle usa el mismo alcance que el listado, y un
 * recibo fuera de alcance no existe para vos). Nómina y Vacaciones sí distinguen. Un componente
 * que impusiera su propia historia de 403 estaría equivocado en Gastos; uno que muestra lo que
 * el backend dice está bien en los tres.
 */

const ICONOS = { permiso: Lock, ausente: SearchX, conexion: WifiOff, error: AlertCircle };

/**
 * ⚠ SE QUEDA MOSTRANDO EL MOTIVO; NO VUELVE SOLA.
 *
 * Quien llegó por un enlace de correo necesita **leer** por qué no puede abrirlo — devolverlo a
 * la lista se lo saca de la pantalla antes de que lo lea. Y resulta ser el cambio **más chico**,
 * no el más grande: las tres pantallas que rebotaban ya tenían el hueco donde va esto
 * (`if (!sheet) return null`), así que llenarlo es menos código que redirigir.
 *
 * ⚠ Todas estas pantallas se renderizan DENTRO de su portal (`PortalLayout` / el shell del
 * módulo), así que el menú sigue ahí: quedarse no deja a nadie encerrado. El botón de volver
 * está igual, pero lo decide la persona.
 */
export default function DetalleNoDisponible({ motivo, onVolver, volverLabel = 'Volver' }) {
  const Icono = ICONOS[motivo?.tipo] || AlertCircle;
  return (
    <div className="page-pad" style={{ padding: '24px 28px', maxWidth: 900, margin: '0 auto' }}>
      {onVolver && (
        <button onClick={onVolver} style={{
          background: 'transparent', border: `1px solid ${BORDER_COLOR}`,
          color: '#475569', borderRadius: 8, padding: '7px 14px',
          fontWeight: 600, fontSize: 13, cursor: 'pointer',
          fontFamily: 'Inter, sans-serif', display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <ArrowLeft size={14} /> {volverLabel}
        </button>
      )}
      <div style={{ textAlign: 'center', padding: '56px 20px' }}>
        <Icono size={34} color="#94a3b8" style={{ marginBottom: 14 }} />
        <div style={{ fontSize: 16, fontWeight: 600, color: '#334155', maxWidth: 520, margin: '0 auto' }}>
          {motivo?.mensaje || 'No se pudo cargar este registro.'}
        </div>
        {motivo?.codigo && (
          <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 10 }}>Error {motivo.codigo}</div>
        )}
      </div>
    </div>
  );
}
